import React, { useState, useEffect, useRef } from 'react';
import { css } from '@emotion/css';
import SelectField from './SelectField';
import { color_shades_dark, color_colors_ocean, color_text_light, color_shades_darker, color_variants_ocean_light_1 } from '../../constants/colors';
import { convert24HourTo12Hour, convert12HourTo24Hour, TimeRange } from '../../utils/weeklyScheduleFormatters';

const addIcon = require('../../content/images/add-circle.svg');
const deleteIcon = require('../../content/images/trash.svg');

interface SingleDayTimeRangeFieldProps {
    name?: string;
    label?: string;
    value?: string; // Backend format string: "09:00,17:00|18:00,22:00" or "00:00,00:01" for closed
    onChange?: (value: string) => void;
    onBlur?: () => void;
    disabled?: boolean;
    className?: string;
    error?: string;
    required?: boolean;
    onValidationChange?: (hasErrors: boolean) => void;
    /** Labels of sibling Single Day fields that this field can copy its hours to. */
    siblingDayLabels?: string[];
    /** Called when the user confirms copying this field's hours to other days. */
    onCopyToOtherDays?: (targetLabels: string[], valueToApply: string) => void;
    defaultValue?: string;
    onReset?: () => void;
}

type SelectionStatus = 'unset' | 'open' | 'closed';

// Modern select styles
const modernSelectStyle = css`
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    width: 100%;
    min-width: 170px;
    margin-bottom: 0;
    
    .Select-control {
        border: 1px solid ${color_shades_dark};
        border-radius: 6px;
        min-height: 44px;
        background: white;
        transition: all 0.2s ease;
        
        &:hover {
            border-color: ${color_colors_ocean};
        }
    }
    
    &.is-focused:not(.is-open) .Select-control {
        border-color: ${color_colors_ocean};
        box-shadow: 0 0 0 3px rgba(66, 153, 225, 0.1);
    }
    
    .Select-placeholder,
    .Select-value {
        padding: 11px 14px;
        font-size: 14px;
        line-height: 1.4;
        display: flex;
        align-items: center;
    }
    
    .Select-value-label {
        color: ${color_text_light};
    }
    
    .Select-input {
        padding: 11px 14px;
        height: 44px;
    }
    
    .Select-input > input {
        padding: 0;
        line-height: 1.4;
        font-size: 14px;
        color: ${color_text_light};
    }
    
    .Select-menu-outer {
        border: 1px solid ${color_shades_dark};
        border-radius: 6px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        margin-top: 4px;
        z-index: 1000;
    }
    
    .Select-menu {
        max-height: 240px;
        border-radius: 6px;
    }
    
    .Select-option {
        padding: 10px 12px;
        font-size: 14px;
        cursor: pointer;
        transition: background 0.15s ease;
        
        &.is-focused {
            background: #f0f4f8;
            color: ${color_text_light};
        }
        
        &.is-selected {
            background: ${color_colors_ocean};
            color: white;
        }
    }
    
    .Select-arrow-zone {
        padding-right: 8px;
    }
    
    .Select-arrow {
        border-color: ${color_text_light} transparent transparent;
        border-width: 5px 4px 2.5px;
    }
    
    &.is-open .Select-arrow {
        border-color: transparent transparent ${color_text_light};
        border-width: 0 4px 5px;
    }
`;

const modernStatusSelectStyle = css`
    ${modernSelectStyle}
`;

const SingleDayTimeRangeField: React.FC<SingleDayTimeRangeFieldProps> = (props) => {
    // ---------------------------------------------------------------------------
    // Overnight helpers
    // "+1" suffix marks an end time that falls on the next calendar day.
    // e.g. internal value "2:00 AM +1" → stored backend time "02:00"
    // ---------------------------------------------------------------------------

    /** Strip the "+1" suffix and return the bare 12-hour time string. */
    const stripNextDay = (timeStr: string): string =>
        timeStr.replace(' +1', '');

    /** Return true if this time value represents a next-day time. */
    const isNextDay = (timeStr: string): boolean =>
        timeStr.endsWith(' +1');

    /**
     * Convert a (possibly +1-suffixed) 12-hour time to minutes since midnight.
     * Next-day times get +1440 (24 * 60) added so they sort after same-day times.
     */
    const timeToMinutes = (timeStr: string): number => {
        const nextDay = isNextDay(timeStr);
        const bare = stripNextDay(timeStr);
        const [timePart, period] = bare.trim().split(' ');
        const [hourStr, minuteStr] = timePart.split(':');
        let hour = parseInt(hourStr, 10);
        const minute = parseInt(minuteStr, 10);
        if (period === 'PM' && hour !== 12) hour += 12;
        if (period === 'AM' && hour === 12) hour = 0;
        return hour * 60 + minute + (nextDay ? 1440 : 0);
    };

    // Parse backend format: Backend sends OPEN hours in 24-hour format
    const parseValue = (val: string | undefined): { status: SelectionStatus; timeRanges: TimeRange[] } => {
        if (val === undefined || val === null || val.trim() === '') {
            return { status: 'unset', timeRanges: [] };
        }

        if (val === 'closed' || val === '00:00,00:01') {
            // Closed all day
            return { status: 'closed', timeRanges: [] };
        }

        // Parse open periods from backend
        const openTimeRanges: TimeRange[] = [];
        const ranges = val.split('|');

        for (const range of ranges) {
            const [start, end] = range.split(',');
            if (start && end) {
                const start24 = start.trim();
                const end24 = end.trim();
                // Detect overnight: if end is numerically before start (e.g. "22:00,02:00")
                // Must use numeric comparison — string comparison can be wrong for legacy/invalid
                // non-zero-padded inputs (e.g. "16:00" < "7:00"), even though valid backend times are zero-padded.
                const toMinutes = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
                const isOvernight = toMinutes(end24) < toMinutes(start24);
                const endTime12 = convert24HourTo12Hour(end24);
                openTimeRanges.push({
                    id: `time_${Date.now()}_${Math.floor(Math.random() * 1e6)}`,
                    startTime: convert24HourTo12Hour(start24),
                    endTime: isOvernight ? `${endTime12} +1` : endTime12
                });
            }
        }

        return {
            status: openTimeRanges.length > 0 ? 'open' : 'unset',
            timeRanges: openTimeRanges
        };
    };

    // Calculate closed hours from open hours (overnight-aware)
    const calculateClosedHours = (openTimeRanges: TimeRange[]): string => {
        if (openTimeRanges.length === 0) {
            return 'Closed All Day (12:00 AM-11:59 PM)';
        }

        /**
         * Format a minutes-since-midnight value back to a readable time string.
         * Values >= 1440 are next-day and get a "(next day)" suffix.
         */
        const minsToLabel = (mins: number): string => {
            const effectiveMins = mins >= 1440 ? mins - 1440 : mins;
            const h = Math.floor(effectiveMins / 60);
            const m = effectiveMins % 60;
            const period = h >= 12 ? 'PM' : 'AM';
            const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
            const label = `${displayHour}:${m.toString().padStart(2, '0')} ${period}`;
            return mins >= 1440 ? `${label} (next day)` : label;
        };

        const closedPeriods: string[] = [];
        const sortedRanges = [...openTimeRanges].sort((a, b) =>
            timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
        );

        const startOfDay = 0;          // 12:00 AM in minutes
        const endOfDay = 23 * 60 + 59; // 11:59 PM in minutes

        // Determine the effective end of the schedule (might be past midnight)
        const lastEndMins = timeToMinutes(sortedRanges[sortedRanges.length - 1].endTime);
        const scheduleEnd = Math.max(endOfDay, lastEndMins);

        // Check if closed from midnight to first opening
        const firstStartMins = timeToMinutes(sortedRanges[0].startTime);
        if (firstStartMins > startOfDay) {
            closedPeriods.push(`${minsToLabel(startOfDay)}-${minsToLabel(firstStartMins)}`);
        }

        // Check gaps between open periods
        for (let i = 0; i < sortedRanges.length - 1; i++) {
            const currentEndMins = timeToMinutes(sortedRanges[i].endTime);
            const nextStartMins = timeToMinutes(sortedRanges[i + 1].startTime);
            if (currentEndMins < nextStartMins) {
                closedPeriods.push(`${minsToLabel(currentEndMins)}-${minsToLabel(nextStartMins)}`);
            }
        }

        // Check if closed from last closing to end of schedule day
        if (lastEndMins < scheduleEnd) {
            closedPeriods.push(`${minsToLabel(lastEndMins)}-${minsToLabel(scheduleEnd)}`);
        }

        return closedPeriods.length > 0 ? `Closed: ${closedPeriods.join(', ')}` : 'Open 24 Hours';
    };

    // Convert to backend format (sends OPEN hours in 24-hour format)
    const convertToBackendFormat = (isOpen: boolean, timeRanges: TimeRange[]): string => {
        if (!isOpen || timeRanges.length === 0) {
            // Closed all day = 00:00,00:01
            return '00:00,00:01';
        }

        // Sort time ranges by start time (using minutes-since-midnight, overnight-aware)
        const sortedRanges = [...timeRanges].sort((a, b) =>
            timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
        );

        // Convert each open period to 24-hour format, stripping +1 suffix from end times
        const openPeriods = sortedRanges.map(range => {
            const start = convert12HourTo24Hour(stripNextDay(range.startTime));
            const end = convert12HourTo24Hour(stripNextDay(range.endTime));
            return `${start},${end}`;
        });

        return openPeriods.join('|');
    };

    const initialValue = parseValue(props.value);
    const [selectionStatus, setSelectionStatus] = useState<SelectionStatus>(initialValue.status);
    const [timeRanges, setTimeRanges] = useState<TimeRange[]>(initialValue.timeRanges);
    const [selectedCopyTargets, setSelectedCopyTargets] = useState<string[]>([]);

    // Ref to prevent infinite loops from internal updates
    const isUpdatingRef = useRef<boolean>(false);
    // Ref to track previous validation state to avoid redundant callbacks
    const prevHasErrorsRef = useRef<boolean | undefined>(undefined);

    // Sync state with external prop changes
    useEffect(() => {
        // Ignore updates originating from our own onChange
        if (isUpdatingRef.current) {
            isUpdatingRef.current = false;
            return;
        }

        const parsed = parseValue(props.value);
        setSelectionStatus(parsed.status);
        // Preserve existing IDs to avoid remounting SelectField components,
        // which would cause end times to appear blank on re-sync.
        setTimeRanges(prev =>
            parsed.timeRanges.map((range) => {
                const existing = prev.find(p => p.startTime === range.startTime);
                return {
                    ...range,
                    id: existing?.id ?? range.id
                };
            })
        );
    }, [props.value]);

    // Generate time options in 1-minute increments
    const generateStartTimeOptions = () => {
        const times: { value: string; label: string }[] = [];
        const periods = ['AM', 'PM'];

        for (const period of periods) {
            for (let hour = 12; hour >= 1; hour--) {
                for (let minute = 0; minute < 60; minute++) {
                    const minuteStr = minute.toString().padStart(2, '0');
                    const value = `${hour}:${minuteStr} ${period}`;
                    times.push({ value, label: value });
                }
            }
        }

        return times.sort((a, b) => timeToMinutes(a.value) - timeToMinutes(b.value));
    };

    /**
     * End-time options: same as start-time options, PLUS next-day times from
     * 12:00 AM (next day) through 6:00 AM (next day).
     */
    const generateEndTimeOptions = () => {
        const times = generateStartTimeOptions();

        // Append next-day options: 12:00 AM +1 → 6:00 AM +1 (inclusive)
        for (let hour = 0; hour <= 6; hour++) {
            for (let minute = 0; minute < 60; minute++) {
                // Stop after 6:00 AM exactly
                if (hour === 6 && minute > 0) break;
                const minuteStr = minute.toString().padStart(2, '0');
                const period = hour < 12 ? 'AM' : 'PM';
                const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                const bare = `${displayHour}:${minuteStr} ${period}`;
                const value = `${bare} +1`;
                const label = `${bare} (next day)`;
                times.push({ value, label });
            }
        }

        return times;
    };

    const startTimeOptions = generateStartTimeOptions();
    const endTimeOptions = generateEndTimeOptions();

    const updateValue = (newStatus: Exclude<SelectionStatus, 'unset'>, newTimeRanges: TimeRange[]) => {
        setSelectionStatus(newStatus);
        setTimeRanges(newTimeRanges);
        if (props.onChange) {
            // Set ref before calling onChange to prevent useEffect from reacting
            isUpdatingRef.current = true;
            const backendFormat = convertToBackendFormat(newStatus === 'open', newTimeRanges);
            props.onChange(backendFormat);
        }
    };

    const handleStatusChange = (selected: any) => {
        const selectedValue = selected?.value;

        if (selectedValue === 'Open') {
            if (timeRanges.length === 0) {
            // Add default time range when opening
                const defaultRange: TimeRange = {
                    id: `time_${Date.now()}_${Math.floor(Math.random() * 1e6)}`,
                    startTime: '9:00 AM',
                    endTime: '5:00 PM'
                };
                updateValue('open', [defaultRange]);
                return;
            }

            updateValue('open', timeRanges);
            return;
        }

        if (selectedValue === 'Closed') {
            updateValue('closed', []);
        }
    };

    const handleAddTimeRange = () => {
        const newTimeRange: TimeRange = {
            id: `time_${Date.now()}_${Math.floor(Math.random() * 1e6)}`,
            startTime: '9:00 AM',
            endTime: '5:00 PM'
        };
        updateValue('open', [...timeRanges, newTimeRange]);
    };

    const handleRemoveTimeRange = (timeRangeId: string) => {
        updateValue('open', timeRanges.filter(tr => tr.id !== timeRangeId));
    };

    const handleTimeRangeChange = (timeRangeId: string, field: 'startTime' | 'endTime', value: string) => {
        updateValue(
            'open',
            timeRanges.map(tr => (tr.id === timeRangeId ? { ...tr, [field]: value } : tr))
        );
    };

    const handleSelectCopyTarget = (targetLabel: string) => {
        setSelectedCopyTargets(prev =>
            prev.includes(targetLabel)
                ? prev.filter(l => l !== targetLabel)
                : [...prev, targetLabel]
        );
    };

    const handleApplyCopy = () => {
        if (selectedCopyTargets.length === 0) return;
        if (props.onCopyToOtherDays) {
            const currentValue = convertToBackendFormat(selectionStatus === 'open', timeRanges);
            props.onCopyToOtherDays(selectedCopyTargets, currentValue);
        }
        setSelectedCopyTargets([]);
    };

    // Validation - returns a map of timeRangeId to error message
    const getValidationErrors = (): { globalError: string | null; timeRangeErrors: Map<string, string> } => {
        const timeRangeErrors = new Map<string, string>();
        let globalError: string | null = null;

        // A required field must have Open or Closed explicitly selected
        if (props.required && selectionStatus === 'unset') {
            globalError = 'Please select Open or Closed for this day.';
            return { globalError, timeRangeErrors };
        }

        // Check that open status has at least one time range
        if (selectionStatus !== 'open') {
            return { globalError, timeRangeErrors };
        }

        if (timeRanges.length === 0) {
            globalError = 'Please add hours or mark as closed.';
            return { globalError, timeRangeErrors };
        }

        // Check that end time is after start time for each range (overnight-aware)
        for (const range of timeRanges) {
            const startMins = timeToMinutes(range.startTime);
            const endMins = timeToMinutes(range.endTime);
            if (startMins >= endMins) {
                timeRangeErrors.set(range.id, 'End time must be after start time.');
            }
        }

        // Check for overlapping time ranges (overnight-aware)
        if (timeRanges.length > 1 && timeRangeErrors.size === 0) {
            const sortedRanges = [...timeRanges].sort((a, b) =>
                timeToMinutes(a.startTime) - timeToMinutes(b.startTime)
            );

            for (let i = 0; i < sortedRanges.length - 1; i++) {
                const currentEnd = timeToMinutes(sortedRanges[i].endTime);
                const nextStart = timeToMinutes(sortedRanges[i + 1].startTime);

                if (currentEnd > nextStart) {
                    globalError = 'Time ranges cannot overlap.';
                    break;
                }
            }
        }

        return { globalError, timeRangeErrors };
    };

    const { globalError, timeRangeErrors } = getValidationErrors();
    const validationError = props.error || globalError;
    const hasErrors = !!validationError || timeRangeErrors.size > 0;

    // Notify parent of validation state changes - only when state actually changes
    useEffect(() => {
        // Only call if the validation state has actually changed
        if (prevHasErrorsRef.current !== hasErrors) {
            prevHasErrorsRef.current = hasErrors;
            if (props.onValidationChange) {
                props.onValidationChange(hasErrors);
            }
        }
    }, [hasErrors, props.onValidationChange]);

    return (
        <div className={`${containerStyle} ${props.className || ''}`}>
            <div className={fieldHeaderRow}>
                {props.label && <label className={labelStyle}>{props.label}</label>}
                {props.defaultValue !== undefined && props.value !== props.defaultValue && (
                    <button
                        className={resetButtonStyle}
                        type="button"
                        onClick={() => props.onReset ? props.onReset() : props.onChange && props.onChange(props.defaultValue)}
                    >
                        Reset to default
                    </button>
                )}
            </div>
            {validationError && <div className={errorStyle}>{validationError}</div>}

            <div className={dayCard}>
                <div className={dayHeader}>
                    <div className={statusSelectWrapper}>
                        <SelectField
                            name={`${props.name}_status`}
                            placeholder="Select hours"
                            value={selectionStatus === 'unset' ? undefined : selectionStatus === 'open' ? 'Open' : 'Closed'}
                            onChange={handleStatusChange}
                            options={[
                                { value: 'Open', label: 'Open' },
                                { value: 'Closed', label: 'Closed' }
                            ]}
                            disabled={props.disabled}
                            clearable={false}
                            useForm={false}
                            customClassName={modernStatusSelectStyle}
                            hideLabel={true}
                        />
                        {selectionStatus === 'open' && timeRanges.length > 0 && (
                            <div className={closedHoursInfo}>
                                {calculateClosedHours(timeRanges)}
                            </div>
                        )}
                    </div>
                </div>

                {selectionStatus === 'open' && (
                    <div className={timeRangesContainer}>
                        <div className={openHoursLabel}>Open Hours:</div>
                        {timeRanges.map((timeRange, index) => (
                            <div key={timeRange.id}>
                                {timeRangeErrors.has(timeRange.id) && (
                                    <div className={timeRangeErrorStyle}>{timeRangeErrors.get(timeRange.id)}</div>
                                )}
                                <div className={timeRangeRow}>
                                    <div className={timeRangeFields}>
                                        <SelectField
                                            name={`${props.name}_start_${timeRange.id}`}
                                            placeholder="Start time"
                                            value={timeRange.startTime}
                                            onChange={(selected: any) =>
                                                handleTimeRangeChange(timeRange.id, 'startTime', selected?.value || '')
                                            }
                                            options={startTimeOptions}
                                            disabled={props.disabled}
                                            clearable={false}
                                            useForm={false}
                                            customClassName={modernSelectStyle}
                                            hideLabel={true}
                                        />
                                        <span className={timeSeparator}>—</span>
                                        <div className={endTimeWrapper}>
                                            <SelectField
                                                name={`${props.name}_end_${timeRange.id}`}
                                                placeholder="End time"
                                                value={timeRange.endTime}
                                                onChange={(selected: any) =>
                                                    handleTimeRangeChange(timeRange.id, 'endTime', selected?.value || '')
                                                }
                                                options={endTimeOptions}
                                                disabled={props.disabled}
                                                clearable={false}
                                                useForm={false}
                                                customClassName={modernSelectStyle}
                                                hideLabel={true}
                                            />
                                            {isNextDay(timeRange.endTime) && (
                                                <span className={nextDayBadge}>next day</span>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className={deleteButton}
                                        onClick={() => handleRemoveTimeRange(timeRange.id)}
                                        disabled={props.disabled}
                                        title="Remove time range"
                                    >
                                        <img src={deleteIcon} alt="Remove" />
                                    </button>
                                </div>
                            </div>
                        ))}
                        <button
                            type="button"
                            className={addTimeRangeButton}
                            onClick={handleAddTimeRange}
                            disabled={props.disabled}
                        >
                            <img src={addIcon} alt="Add" />
                            Add time range
                        </button>

                        {props.siblingDayLabels && props.siblingDayLabels.length > 0 && timeRanges.length > 0 && (
                            <div className={copySection}>
                                <div className={copyTitle}>Copy these hours to:</div>
                                <div className={copyTargetsStyle}>
                                    {props.siblingDayLabels.map(dayLabel => (
                                        <label key={dayLabel} className={copyTargetItem}>
                                            <input
                                                type="checkbox"
                                                checked={selectedCopyTargets.includes(dayLabel)}
                                                onChange={() => handleSelectCopyTarget(dayLabel)}
                                                disabled={props.disabled}
                                            />
                                            <span>{dayLabel}</span>
                                        </label>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    className={applyButton}
                                    disabled={props.disabled || selectedCopyTargets.length === 0}
                                    onClick={handleApplyCopy}
                                >
                                    Apply to selected days
                                </button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const containerStyle = css`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const fieldHeaderRow = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0;
`;

const resetButtonStyle = css`
    background: none;
    border: none;
    color: ${color_colors_ocean};
    font-family: Muli;
    font-size: 12px;
    cursor: pointer;
    padding: 4px 8px;
    border-radius: 4px;
    text-decoration: none;
    margin-left: auto;

    &:hover {
        background-color: ${color_variants_ocean_light_1};
    }
`;

const labelStyle = css`
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 12px;
    color: ${color_shades_darker};
`;

const errorStyle = css`
    font-size: 14px;
    color: #d32f2f;
    margin-bottom: 12px;
    padding: 8px 12px;
    background-color: #ffebee;
    border-radius: 4px;
    border-left: 4px solid #d32f2f;
`;

const timeRangeErrorStyle = css`
    font-size: 13px;
    color: #d32f2f;
    margin-bottom: 8px;
    padding: 6px 10px;
    background-color: #ffebee;
    border-radius: 4px;
    border-left: 3px solid #d32f2f;
`;

const dayCard = css`
    border: 1px solid ${color_shades_dark};
    border-radius: 8px;
    padding: 32px;
    background: white;
`;

const dayHeader = css`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 0;
    padding-left: 4px;
`;

const statusSelectWrapper = css`
    min-width: 140px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const closedHoursInfo = css`
    font-size: 12px;
    color: ${color_text_light};
    padding: 4px 8px;
    background: #f8f9fa;
    border-radius: 4px;
    font-style: italic;
`;

const timeRangesContainer = css`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 12px;
`;

const openHoursLabel = css`
    font-size: 13px;
    font-weight: 600;
    color: ${color_text_light};
    margin-bottom: 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const timeRangeRow = css`
    display: flex;
    align-items: flex-start;
    gap: 12px;
`;

const timeRangeFields = css`
    flex: 1;
    display: flex;
    align-items: flex-start;
    gap: 12px;
`;

const timeSeparator = css`
    font-size: 16px;
    color: ${color_text_light};
    flex-shrink: 0;
    /* Vertically align the dash with the center of the select controls (44px tall) */
    margin-top: 13px;
`;

const endTimeWrapper = css`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    min-width: 0;
`;

const nextDayBadge = css`
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    font-weight: 600;
    color: ${color_colors_ocean};
    background: rgba(66, 153, 225, 0.1);
    border: 1px solid rgba(66, 153, 225, 0.25);
    border-radius: 3px;
    padding: 2px 7px;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    white-space: nowrap;
`;

const deleteButton = css`
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.6;
    transition: opacity 0.2s;
    /* Top-align with the select controls (44px tall, button is ~36px) */
    margin-top: 4px;
    
    &:hover {
        opacity: 1;
    }
    
    &:disabled {
        cursor: not-allowed;
        opacity: 0.3;
    }
    
    img {
        width: 20px;
        height: 20px;
    }
`;

const addTimeRangeButton = css`
    background: transparent;
    border: 1px dashed ${color_shades_dark};
    border-radius: 4px;
    padding: 8px 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 14px;
    color: ${color_colors_ocean};
    transition: all 0.2s;
    
    &:hover {
        background: #f0f0f0;
        border-color: ${color_colors_ocean};
    }
    
    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }
    
    img {
        width: 16px;
        height: 16px;
    }
`;

const copySection = css`
    border-top: 1px solid ${color_shades_dark};
    margin-top: 12px;
    padding-top: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const copyTitle = css`
    font-size: 13px;
    font-weight: 600;
    color: ${color_text_light};
`;

const copyTargetsStyle = css`
    display: flex;
    flex-wrap: wrap;
    gap: 10px 16px;
`;

const copyTargetItem = css`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: ${color_text_light};
    user-select: none;
    input {
        margin: 0;
    }
`;

const applyButton = css`
    align-self: flex-start;
    background: ${color_colors_ocean};
    color: white;
    border: none;
    border-radius: 4px;
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover:not(:disabled) {
        opacity: 0.85;
    }

    &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
    }
`;

export default SingleDayTimeRangeField;
